/** @jsx jsx */
import { type SerializedStyles, jsx } from '@emotion/react';

import { useMemo } from 'react';

import { get as getUser } from 'js/lib/user';

import { CoachChat } from 'bundles/ai-coach-platform/components/patterns';
import { ChatEmptyState } from 'bundles/ai-coach-platform/components/patterns/chat/common';
import { firstNameFromFullName } from 'bundles/ai-coach-platform/utils/nameParser';
import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import { useEnterpriseAdminCoach } from 'bundles/enterprise-admin/EnterpriseAdminCoachContext';
import type { ThirdPartyOrganization } from 'bundles/enterprise-admin/types';

import _t from 'i18n!nls/admin-dashboard';

type Props = {
  thirdPartyOrganization: ThirdPartyOrganization;
  toggleCoach: () => void;
  style?: SerializedStyles;
};

const COACH_MODE_ID = 'ENTERPRISE_ADMIN';

const AdminCoach = ({ thirdPartyOrganization, toggleCoach, style }: Props) => {
  const { currentTimePeriod } = useEnterpriseAdminCoach();
  const { fullName: userName } = getUser();
  const userFirstName = userName ? firstNameFromFullName(userName) : null;
  const displayTitle = _t('Hi #{userFirstName}!', { userFirstName: userFirstName || 'Admin' });
  const displaySubtitle = _t('How can I help you manage your organization today?');

  const contextConfig = useMemo(
    () => ({
      config: {
        coachModeId: COACH_MODE_ID,
        coachActionId: 'ASSISTANT',
      },
      additionalContext: [
        {
          contextKey: 'additionalContext.enterprise_organization_id',
          contextValue: thirdPartyOrganization.id,
        },
        {
          contextKey: 'additionalContext.enterprise_organization_slug',
          contextValue: thirdPartyOrganization.slug,
        },
        {
          contextKey: 'additionalContext.time_period_filter_enterprise_admin_home',
          contextValue: currentTimePeriod,
        },
      ],
      courseraContext: {},
    }),
    [thirdPartyOrganization.id, thirdPartyOrganization.slug, currentTimePeriod]
  );

  return (
    <ErrorBoundaryWithTagging componentName="EnterpriseAdminCoach">
      <CoachChat
        // Key prop forces complete re-render when time period changes, ensuring fresh context
        key={`coach-chat-${currentTimePeriod}`}
        mode={COACH_MODE_ID}
        showSettings={true}
        enableRichInputField={true}
        contextConfig={contextConfig}
        trackingData={{
          mode: COACH_MODE_ID,
          location: 'toolbar',
        }}
        emptyState={<ChatEmptyState title={displayTitle} subtitle={displaySubtitle} />}
        enableIntentionRouting={true}
        enableTextStreaming={true}
        enableFindInChat={true}
        enableContinuousChat={true}
        onClose={toggleCoach}
        additionalStyles={style}
        data-testid="EnterpriseAdminCoach"
      />
    </ErrorBoundaryWithTagging>
  );
};

export default AdminCoach;
